import { useTranslation } from 'react-i18next';
import AssetDetailSection from './AssetDetailSection';
import SelectField from '../../../components/ui/SelectField';
import { AdminProduct } from '../../../api/group/groupAdminApiSlice';

/** The slice of the asset detail's `productInfo` this section needs. */
export interface AssignableProductInfo {
    id: string;
    name: string | null;
    useCase: string | null;
    /** How many Group cells share this product — > 1 means the price is
     *  maintained at the product, and the page's price editor locks. */
    cellCount: number;
    /** #2923: the category the product is filed under; null = none. */
    category?: { id: string, name: string | null } | null;
}

interface ProductAssignmentSectionProps {
    /** The section renders in ROOM scope only — the visibility decision lives
     *  here rather than at the call site, which sits at its complexity gate. */
    editScope: 'full' | 'object';
    productInfo: AssignableProductInfo | null;
    /** The tenant's products, unfiltered — this section narrows to the
     *  object's use case, because a cell cannot change its use case by
     *  changing its product (backend guard mirrors this). */
    products: AdminProduct[];
    mode: 'view' | 'edit';
    /** The pending selection, or null for "unchanged". */
    value: string | null;
    onChange: (productId: string) => void;
}

/**
 * Room-scope only: which Product this object belongs to, and — in edit mode —
 * the control that assigns it to an EXISTING product (pricing stream slice 1;
 * ADR-0026 §N named this surface as missing, not hidden).
 *
 * Assigning is how "one price per apartment type, maintained once" becomes
 * expressible: several rooms point at one shared product, whose default
 * Offer then prices all of them (ADR-0025 amendment 2026-09-25).
 */
const ProductAssignmentSection = ({
    editScope, productInfo, products, mode, value, onChange,
}: ProductAssignmentSectionProps) => {
    const { t } = useTranslation();
    if (editScope !== 'full' || !productInfo) return null;

    const selectable = products.filter((p) =>
        p.id === productInfo.id
        || !productInfo.useCase
        || !p.useCase
        || p.useCase.toLowerCase() === productInfo.useCase.toLowerCase());
    const options = selectable.map((p) => p.id);
    const optionLabels: Record<string, string> = {};
    for (const p of selectable) {
        optionLabels[p.id] = p.name ?? t('common.missing');
    }
    if (!(productInfo.id in optionLabels)) {
        // The current product always stays selectable, even when the list
        // query has not answered yet — an empty select would read as "no
        // product", which is not this object's state.
        options.unshift(productInfo.id);
        optionLabels[productInfo.id] = productInfo.name ?? t('common.missing');
    }

    // The object keeps its own name; what it belongs to is shown next to it —
    // the product (its type) and the category above that (#2923, PO
    // 2026-09-28). Shown in both modes: the category is the product's, set in
    // Services, never here.
    const categoryLine = (
        <p className={'text-xs text-foreground/60'}>
            {t('asset_detail.product.category_label')}
            {': '}
            <span className={'text-foreground'}>
                {productInfo.category?.name ?? t('common.not_applicable')}
            </span>
        </p>
    );

    // Always shown, one object or many: prices are never set on the object
    // (PO, 2026-09-28; #2923), so every room page says where they come from.
    const pricingHint = (
        <p
            className={'text-xs text-foreground/60 px-3 py-2 rounded-[5px]'
                + ' border border-foreground/20 bg-foreground/5'}
        >
            {t('asset_detail.product.pricing_shared_hint', {
                name: productInfo.name ?? '',
            })}
        </p>
    );

    return (
        <AssetDetailSection title={t('asset_detail.sections.product')}>
            {mode === 'edit' ? (
                <div className={'flex flex-col gap-2 max-w-[420px]'}>
                    <SelectField
                        value={value ?? productInfo.id}
                        onChange={onChange}
                        options={options}
                        optionLabels={optionLabels}
                        label={t('asset_detail.product.select_label')}
                    />
                    <p className={'text-xs text-foreground/60'}>
                        {t('asset_detail.product.assign_hint')}
                    </p>
                    {categoryLine}
                    {pricingHint}
                </div>
            ) : (
                <div className={'flex flex-col gap-1'}>
                    <p className={'text-sm text-foreground'}>
                        {productInfo.name ?? t('common.missing')}
                    </p>
                    {categoryLine}
                    {productInfo.cellCount > 1 && (
                        <p className={'text-xs text-foreground/60'}>
                            {t('asset_detail.product.shared_count', {
                                count: productInfo.cellCount,
                            })}
                        </p>
                    )}
                    {pricingHint}
                </div>
            )}
        </AssetDetailSection>
    );
};

export default ProductAssignmentSection;
